/**
 * CategoryNode — one recursive node of the marketplace category tree
 * (STORY-003-007).
 *
 * Anatomy: a `role="treeitem"` row carrying the WAI-ARIA Tree attributes
 * (`aria-level`, `aria-expanded` when it has children, `aria-selected`, and
 * `aria-current="page"` on the active node). A parent renders a chevron
 * (Lucide `ChevronRight`, rotated when expanded) whose click toggles
 * expand/collapse WITHOUT navigating (AC#4); the label button is the
 * navigation target and fires `onSelect` (AC#3). Children render in a nested
 * `role="group"` `<ul>` so the tree structure is exposed to AT.
 *
 * Keyboard (WAI-ARIA Tree pattern, AC#7): the treeitem `<li>` is the focusable
 * unit (roving `tabIndex`). ArrowUp/ArrowDown move focus between the *visible*
 * treeitems; ArrowRight expands a collapsed parent (or steps into the first
 * child); ArrowLeft collapses an expanded parent (or steps to the parent). The
 * traversal needs the full visible-order list, so it is owned by the parent
 * `CategoryTree` and reached here via `onTreeKeyDown(event, nodeId)` bound to
 * the row's `onKeyDown`. `focusedId` drives the roving `tabIndex`.
 *
 * Tokens come from the Tailwind `--g-*` theme mirror (no hand-rolled colors);
 * the active row reuses the `primary-soft` / `primary-ink` pairing established
 * by the chat ThreadListItem so the marketplace sidebar reads as one system.
 * The chevron's expand/collapse aria-label comes from the `marketplace` i18n
 * namespace.
 *
 * 2026-08 (counter slip): the rows are square and edge-to-edge rather than
 * rounded pills floating in a gap, because `CategoryTree` now draws a real rule
 * between root rows — a category list is a ledger of departments, and the
 * shipped version rendered it as nine lines of unstructured plain text. The
 * active row is carried by fill + ink + weight + `aria-current`, deliberately
 * NOT by a thick coloured left border. Rows are 44px so a thumb can hit them.
 */
import { ChevronRight } from "lucide-react";
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { iconProps } from "@/lib/icons";

import {
  categoryDisplayName,
  type Category,
} from "@/features/marketplace/useCategoryTree";

export interface CategoryNodeProps {
  node: Category;
  /** 1-based tree depth for `aria-level` (root nodes are level 1). */
  level: number;
  activeCategoryId: string | null;
  /** Id of the treeitem that currently owns the roving `tabIndex`. */
  focusedId: string | null;
  /** Ids of nodes whose children are currently shown. */
  expandedIds: ReadonlySet<string>;
  onSelect: (categoryId: string) => void;
  onToggle: (categoryId: string) => void;
  /** WAI-ARIA Tree keyboard handler owned by CategoryTree. */
  onTreeKeyDown: (
    event: React.KeyboardEvent<HTMLElement>,
    categoryId: string,
  ) => void;
  /** Track focus so the roving tabIndex follows arrow-key movement. */
  onNodeFocus: (categoryId: string) => void;
}

export function CategoryNode({
  node,
  level,
  activeCategoryId,
  focusedId,
  expandedIds,
  onSelect,
  onToggle,
  onTreeKeyDown,
  onNodeFocus,
}: CategoryNodeProps): ReactElement {
  const { t, i18n } = useTranslation("marketplace");
  const hasChildren = node.children.length > 0;
  const isExpanded = hasChildren && expandedIds.has(node.id);
  const isActive = node.id === activeCategoryId;
  const label = categoryDisplayName(node, i18n.language);
  const toggleLabel = isExpanded
    ? t("category.collapse", { name: label })
    : t("category.expand", { name: label });

  // Indent grows with depth but is capped so a 4-deep tree never overflows at
  // 320px (story risk: "renders 4-deep without indent overflow at 320px").
  const indentRem = Math.min(level - 1, 4) * 0.75;

  return (
    <li
      role="treeitem"
      aria-level={level}
      aria-selected={isActive}
      aria-expanded={hasChildren ? isExpanded : undefined}
      aria-current={isActive ? "page" : undefined}
      // Roving tabIndex: exactly one treeitem is in the tab order at a time.
      tabIndex={focusedId === node.id ? 0 : -1}
      onKeyDown={(e) => onTreeKeyDown(e, node.id)}
      onFocus={(e) => {
        // Only react to focus landing on THIS row, not a descendant treeitem.
        if (e.target === e.currentTarget) onNodeFocus(node.id);
      }}
      data-testid={`category-node-${node.id}`}
      data-category-id={node.id}
      className="select-none outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
    >
      <div
        className={
          "flex min-h-[44px] items-center gap-1 pr-2 transition-colors " +
          (isActive
            ? "bg-[color:var(--g-primary-soft)] font-semibold text-[color:var(--g-primary-ink)]"
            : "text-foreground hover:bg-[color:var(--g-sunken)]")
        }
        style={{ paddingLeft: `${indentRem}rem` }}
      >
        {hasChildren ? (
          <button
            type="button"
            // The chevron toggles expansion only — it must NOT navigate (AC#4).
            onClick={(e) => {
              e.stopPropagation();
              onToggle(node.id);
            }}
            // Keep the chevron out of the tab order: the treeitem <li> is the
            // single focusable/selectable unit (roving tabIndex). Its expanded
            // state is already mirrored on the treeitem's aria-expanded.
            tabIndex={-1}
            aria-label={toggleLabel}
            data-testid={`category-toggle-${node.id}`}
            className="flex size-8 shrink-0 items-center justify-center rounded-[6px] text-[color:var(--g-ink-2)] transition-colors hover:bg-[color:var(--g-rule)] hover:text-foreground"
          >
            <ChevronRight
              {...iconProps("sm")}
              className={
                "transition-transform motion-reduce:transition-none " +
                (isExpanded ? "rotate-90" : "")
              }
            />
          </button>
        ) : (
          // Spacer keeps leaf labels aligned with their expandable siblings.
          <span className="size-8 shrink-0" aria-hidden="true" />
        )}

        <button
          type="button"
          // Label is presentational within the treeitem: it triggers selection
          // but is not itself a tab stop (the treeitem owns focus).
          tabIndex={-1}
          onClick={() => onSelect(node.id)}
          data-testid={`category-label-${node.id}`}
          className="flex-1 truncate py-2.5 text-left text-sm"
        >
          {label}
        </button>
      </div>

      {hasChildren && isExpanded && (
        <ul role="group" className="m-0 list-none p-0">
          {node.children.map((child) => (
            <CategoryNode
              key={child.id}
              node={child}
              level={level + 1}
              activeCategoryId={activeCategoryId}
              focusedId={focusedId}
              expandedIds={expandedIds}
              onSelect={onSelect}
              onToggle={onToggle}
              onTreeKeyDown={onTreeKeyDown}
              onNodeFocus={onNodeFocus}
            />
          ))}
        </ul>
      )}
    </li>
  );
}
